import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Input,
    Button,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader";
import Swal from "sweetalert2";
import { getContact, saveContact } from "api/ApiServices";

import {
    FiHeadphones,
    FiMail,
    FiPhone,
    FiMapPin,
    FiEdit3,
    FiSave,
    FiUser,
    FiGlobe,
    FiCheckCircle,
    FiLoader,
} from "react-icons/fi";

const ContactUs = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [contactData, setContactData] = useState({
        name: "",
        email: "",
        phone: "",
        countryCode: "",
        address: "",
    });

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        countryCode: "",
        address: "",
    });

    useEffect(() => {
        fetchContact();
    }, []);

    const fetchContact = async () => {
        setLoading(true);

        try {
            const response = await getContact();

            console.log("Fetch response:", response);

            if (response?.success && response?.data) {
                const data = response.data;

                setContactData({
                    name: data.name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    countryCode: data.countryCode || "",
                    address: data.address || "",
                });
            } else {
                console.log(
                    "No contact data found or error:",
                    response?.message
                );
            }
        } catch (error) {
            console.error("Error fetching contact details:", error);
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // Field Validation
    // =========================

    const validateField = (field, value) => {
        const newErrors = { ...errors };

        switch (field) {
            case "name": {
                const name = value.trim();

                if (!name) {
                    newErrors.name = "Support Title is required.";
                } else if (name.length < 2) {
                    newErrors.name =
                        "Support Title must be at least 2 characters.";
                } else if (name.length > 100) {
                    newErrors.name =
                        "Support Title cannot exceed 100 characters.";
                } else {
                    newErrors.name = "";
                }

                break;
            }

            case "email": {
                const email = value.trim();

                if (!email) {
                    newErrors.email = "Email is required.";
                } else if (
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                ) {
                    newErrors.email =
                        "Please enter a valid email address.";
                } else {
                    newErrors.email = "";
                }

                break;
            }

            case "countryCode": {
                const countryCode = value.trim();

                if (!countryCode) {
                    newErrors.countryCode =
                        "Country code is required.";
                } else if (
                    !/^\+[1-9][0-9]{0,3}$/.test(countryCode)
                ) {
                    newErrors.countryCode =
                        "Enter a valid country code, e.g. +91.";
                } else {
                    newErrors.countryCode = "";
                }

                break;
            }

            case "phone": {
                const phone = value.trim();

                if (!phone) {
                    newErrors.phone = "Phone number is required.";
                } else if (!/^[0-9]{7,15}$/.test(phone)) {
                    newErrors.phone =
                        "Phone number must contain 7-15 digits.";
                } else {
                    newErrors.phone = "";
                }

                break;
            }

            case "address": {
                const address = value.trim();

                if (!address) {
                    newErrors.address = "Address is required.";
                } else if (address.length < 5) {
                    newErrors.address =
                        "Address must be at least 5 characters.";
                } else if (address.length > 250) {
                    newErrors.address =
                        "Address cannot exceed 250 characters.";
                } else {
                    newErrors.address = "";
                }

                break;
            }

            default:
                break;
        }

        setErrors(newErrors);
    };

    // =========================
    // Handle Input Change
    // =========================

    const handleChange = (field, value) => {
        setContactData((prev) => ({
            ...prev,
            [field]: value,
        }));

        validateField(field, value);
    };

    // =========================
    // Handle Name
    // =========================

    const handleNameChange = (e) => {
        const value = e.target.value.slice(0, 100);

        handleChange("name", value);
    };

    // =========================
    // Handle Email
    // =========================

    const handleEmailChange = (e) => {
        const value = e.target.value.slice(0, 150);

        handleChange("email", value);
    };

    // =========================
    // Handle Country Code
    // =========================

    const handleCountryCodeChange = (e) => {
        let value = e.target.value;

        // Remove everything except numbers and +
        value = value.replace(/[^0-9+]/g, "");

        // Only one + is allowed
        if (value.includes("+")) {
            value =
                "+" +
                value.replace(/\+/g, "");
        }

        // + must be at beginning
        if (!value.startsWith("+") && value.length > 0) {
            value = "+" + value;
        }

        // Country code maximum 4 digits after +
        if (value.startsWith("+")) {
            value = "+" + value.slice(1).slice(0, 3);
        }

        handleChange("countryCode", value);
    };

    // =========================
    // Handle Phone
    // =========================

    const handlePhoneChange = (e) => {
        const onlyNumbers = e.target.value
            .replace(/[^0-9]/g, "")
            .slice(0, 15);

        handleChange("phone", onlyNumbers);
    };

    // =========================
    // Handle Address
    // =========================

    const handleAddressChange = (e) => {
        const value = e.target.value.slice(0, 250);

        handleChange("address", value);
    };

    // =========================
    // Validate All Fields
    // =========================

    const validateForm = () => {
        const newErrors = {
            name: "",
            email: "",
            phone: "",
            countryCode: "",
            address: "",
        };

        const name = contactData.name?.trim() || "";
        const email = contactData.email?.trim() || "";
        const phone = contactData.phone?.toString().trim() || "";
        const countryCode =
            contactData.countryCode?.trim() || "";
        const address = contactData.address?.trim() || "";

        // Name
        if (!name) {
            newErrors.name = "Support Title is required.";
        } else if (name.length < 2) {
            newErrors.name =
                "Support Title must be at least 2 characters.";
        } else if (name.length > 100) {
            newErrors.name =
                "Support Title cannot exceed 100 characters.";
        }

        // Email
        if (!email) {
            newErrors.email = "Email is required.";
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ) {
            newErrors.email =
                "Please enter a valid email address.";
        }

        // Country Code
        if (!countryCode) {
            newErrors.countryCode =
                "Country code is required.";
        } else if (
            !/^\+[1-9][0-9]{0,3}$/.test(countryCode)
        ) {
            newErrors.countryCode =
                "Enter a valid country code, e.g. +91.";
        }

        // Phone
        if (!phone) {
            newErrors.phone = "Phone number is required.";
        } else if (!/^[0-9]{7,15}$/.test(phone)) {
            newErrors.phone =
                "Phone number must contain 7-15 digits.";
        }

        // Address
        if (!address) {
            newErrors.address = "Address is required.";
        } else if (address.length < 5) {
            newErrors.address =
                "Address must be at least 5 characters.";
        } else if (address.length > 250) {
            newErrors.address =
                "Address cannot exceed 250 characters.";
        }

        setErrors(newErrors);

        return !Object.values(newErrors).some(Boolean);
    };

    // =========================
    // Update Contact
    // =========================

    const handleUpdate = async (e) => {
        e.preventDefault();

        const isValid = validateForm();

        if (!isValid) {
            Swal.fire({
                title: "Validation Error!",
                text: "Please correct the highlighted fields.",
                icon: "error",
                confirmButtonText: "OK",
            });

            return;
        }

        setSaving(true);

        try {
            const payload = {
                name: contactData.name.trim(),
                email: contactData.email.trim(),
                phone: contactData.phone.trim(),
                countryCode: contactData.countryCode.trim(),
                address: contactData.address.trim(),
            };

            console.log("Saving payload:", payload);

            const response = await saveContact(payload);

            console.log("Save response:", response);

            if (response?.success) {
                Swal.fire({
                    title:
                        response?.message ||
                        "Contact Us updated successfully",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                }).then(() => {
                    fetchContact();
                });
            } else {
                Swal.fire(
                    "Error",
                    response?.message ||
                    "Failed to update contact.",
                    "error"
                );
            }
        } catch (error) {
            console.error("Error saving contact:", error);

            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while saving contact details.";

            Swal.fire(
                "Error",
                errorMessage,
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <CommonHeader />

            <Container className="mt--7" fluid>
                <Row>
                    {/* ================= LEFT CONTACT CARD ================= */}
                    <Col
                        xl="4"
                        className="order-xl-2 mb-5 mb-xl-0"
                    >
                        <Card className="shadow border-0 h-100">
                            <CardHeader
                                className="bg-transparent border-0 pb-0"
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h3 className="mb-1 text-dark font-weight-700">
                                            <FiHeadphones
                                                className="mr-2"
                                                size={20}
                                                style={{
                                                    color: "var(--button-primary)",
                                                    verticalAlign: "middle",
                                                }}
                                            />
                                            Contact Information
                                        </h3>

                                        <p className="text-muted mb-0 small">
                                            Public support contact details
                                        </p>
                                    </div>

                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            background:
                                                "var(--button-primary)",
                                        }}
                                    >
                                        <FiHeadphones
                                            size={20}
                                            className="text-white"
                                        />
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Contact Icon */}
                                <div className="text-center mb-4">
                                    <div
                                        className="mx-auto d-flex align-items-center justify-content-center rounded-circle shadow"
                                        style={{
                                            width: "110px",
                                            height: "110px",
                                            background:
                                                "linear-gradient(135deg, var(--button-primary), var(--button-secondary))",
                                        }}
                                    >
                                        <FiHeadphones
                                            size={46}
                                            className="text-white"
                                        />
                                    </div>
                                </div>

                                {/* Support Name */}
                                <div className="text-center mb-4">
                                    <h2 className="mb-1 text-dark font-weight-700">
                                        {contactData.name ||
                                            "Global Support"}
                                    </h2>

                                    <p className="text-muted mb-0">
                                        Customer Support
                                    </p>
                                </div>

                                <hr className="my-4" />

                                {/* Email */}
                                <div className="d-flex align-items-start mb-4">
                                    <div
                                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                                        style={{
                                            minWidth: "40px",
                                            height: "40px",
                                            background:
                                                "rgba(0, 123, 255, 0.1)",
                                        }}
                                    >
                                        <FiMail
                                            size={19}
                                            style={{
                                                color:
                                                    "var(--button-primary)",
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <small className="text-muted d-block mb-1">
                                            Email Address
                                        </small>

                                        <span className="font-weight-600 text-dark">
                                            {contactData.email ||
                                                "support@cradleai.com"}
                                        </span>
                                    </div>
                                </div>

                                {/* Phone */}
                                <div className="d-flex align-items-start mb-4">
                                    <div
                                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                                        style={{
                                            minWidth: "40px",
                                            height: "40px",
                                            background:
                                                "rgba(40, 167, 69, 0.1)",
                                        }}
                                    >
                                        <FiPhone
                                            size={19}
                                            className="text-success"
                                        />
                                    </div>

                                    <div>
                                        <small className="text-muted d-block mb-1">
                                            Phone Number
                                        </small>

                                        <span className="font-weight-600 text-dark">
                                            {contactData.countryCode &&
                                                contactData.phone
                                                ? `${contactData.countryCode} ${contactData.phone}`
                                                : "Not available"}
                                        </span>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="d-flex align-items-start">
                                    <div
                                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                                        style={{
                                            minWidth: "40px",
                                            height: "40px",
                                            background:
                                                "rgba(255, 193, 7, 0.12)",
                                        }}
                                    >
                                        <FiMapPin
                                            size={19}
                                            className="text-warning"
                                        />
                                    </div>

                                    <div>
                                        <small className="text-muted d-block mb-1">
                                            Address
                                        </small>

                                        <span className="font-weight-600 text-dark">
                                            {contactData.address ||
                                                "Main Headquarters"}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className="mt-5 p-3 rounded"
                                    style={{
                                        background:
                                            "rgba(0, 0, 0, 0.03)",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FiCheckCircle
                                            className="mr-2"
                                            size={17}
                                            style={{
                                                color:
                                                    "var(--button-primary)",
                                            }}
                                        />

                                        <small className="text-muted">
                                            These details are displayed
                                            publicly to your users.
                                        </small>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* ================= RIGHT EDIT PANEL ================= */}
                    <Col
                        xl="8"
                        className="order-xl-1"
                    >
                        <Card className="shadow border-0">
                            <CardHeader className="bg-transparent border-0 py-4">
                                <Row className="align-items-center">
                                    <Col>
                                        <h3 className="mb-1 text-dark font-weight-700">
                                            <FiEdit3
                                                className="mr-2"
                                                size={21}
                                                style={{
                                                    color:
                                                        "var(--button-primary)",
                                                    verticalAlign:
                                                        "middle",
                                                }}
                                            />
                                            Edit Contact Us
                                        </h3>

                                        <p className="text-muted mb-0 small">
                                            Manage your public support
                                            contact information
                                        </p>
                                    </Col>

                                    <Col xs="auto">
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: "42px",
                                                height: "42px",
                                                background:
                                                    "rgba(0, 123, 255, 0.1)",
                                            }}
                                        >
                                            <FiGlobe
                                                size={20}
                                                style={{
                                                    color:
                                                        "var(--button-primary)",
                                                }}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>

                            <CardBody className="pt-2">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                                        <p className="mt-2">Loading contact information...</p>
                                    </div>
                                ) : (
                                    <Form onSubmit={handleUpdate}>
                                        {/* ================= SUPPORT DETAILS ================= */}
                                        <div className="mb-4">
                                            <div className="d-flex align-items-center mb-3">
                                                <div
                                                    className="mr-2 d-flex align-items-center justify-content-center rounded"
                                                    style={{
                                                        width: "34px",
                                                        height: "34px",
                                                        background:
                                                            "rgba(0, 123, 255, 0.1)",
                                                    }}
                                                >
                                                    <FiUser
                                                        size={17}
                                                        style={{
                                                            color:
                                                                "var(--button-primary)",
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <h6 className="heading-small text-muted mb-0">
                                                        Support Details
                                                    </h6>

                                                    <small className="text-muted">
                                                        Basic public support
                                                        information
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        <Row>
                                            {/* NAME */}
                                            <Col lg="6">
                                                <FormGroup>
                                                    <label
                                                        className="form-control-label"
                                                        htmlFor="input-name"
                                                    >
                                                        Support Title{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Input
                                                        id="input-name"
                                                        type="text"
                                                        placeholder="e.g. CradleAI Help Desk"
                                                        value={
                                                            contactData.name
                                                        }
                                                        maxLength={100}
                                                        onChange={
                                                            handleNameChange
                                                        }
                                                        className={`form-control-alternative ${errors.name
                                                            ? "border-danger"
                                                            : ""
                                                            }`}
                                                    />

                                                    {errors.name && (
                                                        <small className="text-danger mt-1 d-block">
                                                            {errors.name}
                                                        </small>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            {/* EMAIL */}
                                            <Col lg="6">
                                                <FormGroup>
                                                    <label
                                                        className="form-control-label"
                                                        htmlFor="input-email"
                                                    >
                                                        Public Email{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Input
                                                        id="input-email"
                                                        type="email"
                                                        placeholder="support@domain.com"
                                                        value={
                                                            contactData.email
                                                        }
                                                        maxLength={150}
                                                        onChange={
                                                            handleEmailChange
                                                        }
                                                        className={`form-control-alternative ${errors.email
                                                            ? "border-danger"
                                                            : ""
                                                            }`}
                                                    />

                                                    {errors.email && (
                                                        <small className="text-danger mt-1 d-block">
                                                            {errors.email}
                                                        </small>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <Row>
                                            {/* COUNTRY CODE */}
                                            <Col md="4">
                                                <FormGroup>
                                                    <label
                                                        className="form-control-label"
                                                        htmlFor="input-country-code"
                                                    >
                                                        Country Code{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Input
                                                        id="input-country-code"
                                                        type="text"
                                                        placeholder="+91"
                                                        value={
                                                            contactData.countryCode
                                                        }
                                                        maxLength={4}
                                                        onChange={
                                                            handleCountryCodeChange
                                                        }
                                                        className={`form-control-alternative ${errors.countryCode
                                                            ? "border-danger"
                                                            : ""
                                                            }`}
                                                    />

                                                    {errors.countryCode && (
                                                        <small className="text-danger mt-1 d-block">
                                                            {
                                                                errors.countryCode
                                                            }
                                                        </small>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            {/* PHONE */}
                                            <Col md="8">
                                                <FormGroup>
                                                    <label
                                                        className="form-control-label"
                                                        htmlFor="input-phone"
                                                    >
                                                        Phone Number{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Input
                                                        id="input-phone"
                                                        type="text"
                                                        placeholder="9876543210"
                                                        value={
                                                            contactData.phone
                                                        }
                                                        maxLength={15}
                                                        inputMode="numeric"
                                                        onChange={
                                                            handlePhoneChange
                                                        }
                                                        className={`form-control-alternative ${errors.phone
                                                            ? "border-danger"
                                                            : ""
                                                            }`}
                                                    />

                                                    {errors.phone && (
                                                        <small className="text-danger mt-1 d-block">
                                                            {errors.phone}
                                                        </small>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <hr className="my-4" />

                                        {/* ================= LOCATION ================= */}
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center mb-3">
                                                <div
                                                    className="mr-2 d-flex align-items-center justify-content-center rounded"
                                                    style={{
                                                        width: "34px",
                                                        height: "34px",
                                                        background:
                                                            "rgba(255, 193, 7, 0.12)",
                                                    }}
                                                >
                                                    <FiMapPin
                                                        size={17}
                                                        className="text-warning"
                                                    />
                                                </div>

                                                <div>
                                                    <h6 className="heading-small text-muted mb-0">
                                                        Location Information
                                                    </h6>

                                                    <small className="text-muted">
                                                        Public office or support
                                                        address
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <label
                                                        className="form-control-label"
                                                        htmlFor="input-address"
                                                    >
                                                        Address{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Input
                                                        id="input-address"
                                                        type="textarea"
                                                        rows="4"
                                                        placeholder="Enter headquarters or support address"
                                                        value={
                                                            contactData.address
                                                        }
                                                        maxLength={250}
                                                        onChange={
                                                            handleAddressChange
                                                        }
                                                        className={`form-control-alternative ${errors.address
                                                            ? "border-danger"
                                                            : ""
                                                            }`}
                                                    />

                                                    <div className="d-flex justify-content-between mt-1">
                                                        <div>
                                                            {errors.address && (
                                                                <small className="text-danger">
                                                                    {
                                                                        errors.address
                                                                    }
                                                                </small>
                                                            )}
                                                        </div>

                                                        <small className="text-muted">
                                                            {
                                                                contactData
                                                                    .address
                                                                    .length
                                                            }
                                                            /250
                                                        </small>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* ================= ACTION ================= */}
                                        <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                            <Button
                                                type="submit"
                                                color="primary"
                                                disabled={saving}
                                                className="px-4 button-secondary bg-button-gradient-primary text-nowrap"
                                                style={{
                                                    borderRadius: "8px",
                                                    border: "none",
                                                }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <FiLoader
                                                            size={16}
                                                            className="mr-2"
                                                            style={{
                                                                animation:
                                                                    "spin 1s linear infinite",
                                                            }}
                                                        />
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiSave
                                                            size={16}
                                                            className="mr-2"
                                                        />
                                                        Update Details
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <style>
                {`
                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}
            </style>
        </>
    );
};

export default ContactUs;